import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useDebounce } from 'use-debounce'

/**
 * Debounced lookup of whether a customer legal name is unique. Excludes the
 * customer's own record when editing (`customerId`) so an unchanged name isn't
 * flagged as a duplicate. Pass `enabled: false` to opt out entirely — no query
 * runs and the name always reports unique and settled.
 */
export const useCustomerNameIsUnique = (
  props: { legalName: string; enabled: boolean } & (
    | { isANewCustomer: true; customerId?: never }
    | { isANewCustomer: false; customerId: string }
  )
) => {
  const [debouncedCustomerNameSearch] = useDebounce(props.legalName, 750)

  const matchingCustomersQueryEnabled =
    props.enabled && debouncedCustomerNameSearch !== ''

  const matchingCustomersQuery = useQuery(
    dashboard20240730Client.getCustomers,
    { legalName: debouncedCustomerNameSearch, limit: 100 },
    { enabled: matchingCustomersQueryEnabled }
  )

  // Opted out: never surface a query or validating/duplicate signal.
  if (!props.enabled) {
    return { isPending: false, isUnique: true }
  }

  const ownCustomerId = props.isANewCustomer ? undefined : props.customerId
  const isUnique = !(matchingCustomersQuery.data?.items ?? []).some(
    customer =>
      customer.legalName === props.legalName && customer.id !== ownCustomerId
  )

  // While the debounce hasn't caught up, the query still reflects the previous
  // name, so `isUnique` is stale for the current input — report pending so
  // callers keep validation in a validating state rather than trusting it.
  const debounceIsCatchingUp = props.legalName !== debouncedCustomerNameSearch

  return {
    // Guard the query's `isPending` on `enabled`: a disabled query reports
    // `isPending: true` in TanStack Query v5, which would otherwise show the
    // spinner on an empty field and block the name as still-validating.
    isPending:
      debounceIsCatchingUp ||
      (matchingCustomersQueryEnabled && matchingCustomersQuery.isPending),
    isUnique
  }
}
